import { AfterViewInit, Component, inject, Input, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import MapView from '@arcgis/core/views/MapView';
import Graphic from '@arcgis/core/Graphic';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import MapImageLayer from '@arcgis/core/layers/MapImageLayer';
import GroupLayer from '@arcgis/core/layers/GroupLayer';
import Layer from '@arcgis/core/layers/Layer';
import Sublayer from '@arcgis/core/layers/support/Sublayer';
import Point from '@arcgis/core/geometry/Point';
import SimpleMarkerSymbol from '@arcgis/core/symbols/SimpleMarkerSymbol';
import Circle from '@arcgis/core/geometry/Circle';
import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol';
import * as geometryEngine from '@arcgis/core/geometry/geometryEngine';
import * as webMercatorUtils from '@arcgis/core/geometry/support/webMercatorUtils';
import { toolkitIntelligenceLayers } from '../../../configs/Site-Toolkit-filters-config';
import {
  FeatureAttributes,
  SiteIntelligenceLayer,
} from '../../../models/site-intelligence-layer';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { DatePipe, DecimalPipe } from '@angular/common';
import { SiteIntelligenceService } from '../../../services/arcgis/site-intelligence.service';
import { SvgIconComponent } from '../../../../../shared/components/svg-icon/svg-icon.component';
import { ArcgisService } from '../../../services/arcgis/arcgis.service';
import { AuditLogService } from '../../../../../shared/services/audit-log/audit-log.service';
import { LoaderService } from '../../../../../shared/services/loader/loader.service';
@Component({
  selector: 'app-site-toolkit-intelligence',
  standalone: true,
  imports: [DatePipe, DecimalPipe, SvgIconComponent],
  templateUrl: './site-toolkit-intelligence.component.html',
  styleUrls: ['./site-toolkit-intelligence.component.scss'],
})
export class SiteToolkitIntelligenceComponent implements OnInit, AfterViewInit {
  @Input() mapView!: MapView;
  @Input() arcgisToken!: string;
  @Input() scrollToMap = false;

  siteIntelligenceService = inject(SiteIntelligenceService);
  arcgisService = inject(ArcgisService);
  route = inject(ActivatedRoute);
  auditLogService = inject(AuditLogService);
  private loaderService = inject(LoaderService);

  // Direct getters/setters without toolkit title
  get locationDetails(): SiteIntelligenceLayer[] | null {
    return this.siteIntelligenceService.getCurrentData().locationDetails;
  }

  get markedPointAddress(): string | null {
    return this.siteIntelligenceService.getCurrentData().markedPointAddress;
  }

  get expandedCards(): boolean[] {
    return this.siteIntelligenceService.getCurrentData().expandedCards;
  }

  set expandedCards(value: boolean[]) {
    this.siteIntelligenceService.setExpandedCards(value);
  }

  isPinActive = false;
  isLoading: boolean | null = null;

  // Add this property to store feature graphics by layer
  private featureGraphics = new Map<string, Graphic[]>();

  // Add this property to define which layers should be expanded by default
  private defaultExpandedLayers = [
    'CEJST (LIDAC) Presence',
    'EPA IRA Disadvantaged Communities (GBRA)',
    'NMTC Presence',
    'Regrid Nationwide Parcel Data',
    'USA Structures',
  ];

  ngOnInit() {
    this.siteIntelligenceService.clearData();
    // Check for existing point graphics
    const existingPoint = this.mapView.graphics.find(
      (graphic) => graphic.geometry.type === 'point',
    );

    if (existingPoint) {
      const point = existingPoint.geometry as Point;
      // Process existing point
      this.fetchAddress(point).then(async () => {
        this.isLoading = true;
        await this.fetchLocationDetails(point);
        this.isLoading = false;
      });
    } else {
      // Original query params logic
      this.route.queryParams.subscribe((params) => {
        const lat = Number(params['lat']);
        const lon = Number(params['lon']);

        if (!isNaN(lat) && !isNaN(lon)) {
          const point = new Point({
            longitude: lon,
            latitude: lat,
          });

          // Add marker for the point
          const pointGraphic = new Graphic({
            geometry: point,
            symbol: new SimpleMarkerSymbol({
              color: 'blue',
              size: '12px',
              outline: {
                color: 'white',
                width: 1,
              },
            }),
          });

          // Add marker and fetch details
          this.mapView.graphics.removeAll();
          this.mapView.graphics.add(pointGraphic);
          this.siteIntelligenceService.clearData();

          // Center map on point
          this.mapView
            .goTo({
              target: point,
              zoom: 18,
            })
            .then(async () => {
              await this.fetchAddress(point);
              this.isLoading = true;
              await this.fetchLocationDetails(point);
              this.isLoading = false;
            });
        }
      });
    }
  }

  ngAfterViewInit() {
    if (this.scrollToMap) {
      const el = document.getElementById('siteIntelligenceMap');
      if (el) {
        el.scrollIntoView({
          behavior: 'auto',
          block: 'start',
        });
      }
    }
    this.loaderService.hide();
  }

  async activatePin() {
    this.auditLogService.logFeature({
      featureName: 'site_intelligence',
      action: 'click',
      meta: { component: 'site-toolkit-intelligence' },
      description: 'User clicks on site intelligence feature',
    });

    this.isPinActive = true;
    this.mapView.container.style.cursor = 'crosshair';

    const clickHandler = this.mapView.on('click', async (event) => {
      if (!this.isPinActive) return;

      this.isPinActive = false; // Deactivate the pin after selecting a point
      this.mapView.container.style.cursor = 'default';
      clickHandler.remove();

      // Clear existing graphics to remove old marked points
      this.mapView.graphics.removeAll();
      this.siteIntelligenceService.clearData(); // Clear previous results

      const point = new Point({
        longitude: event.mapPoint.longitude,
        latitude: event.mapPoint.latitude,
      });

      // Add a marker for the selected point
      const pointGraphic = new Graphic({
        geometry: point,
        symbol: new SimpleMarkerSymbol({
          color: 'blue',
          size: '12px',
          outline: {
            color: 'white',
            width: 1,
          },
        }),
      });

      // Add the marker to the map
      this.mapView.graphics.add(pointGraphic);

      // Fetch the address of the marked point
      await this.fetchAddress(point);

      // Get the distance and measurement unit from the user input
      const searchDistance = parseFloat(
        (document.getElementById('myRange') as HTMLInputElement).value,
      );
      const measurementUnit = (
        document.querySelector('select') as HTMLSelectElement
      ).value;

      // Create a circle around the selected point
      const circle = new Circle({
        center: point,
        radius: searchDistance,
        radiusUnit: measurementUnit as __esri.CircleProperties['radiusUnit'],
      });

      const circleGraphic = new Graphic({
        geometry: circle,
        symbol: new SimpleFillSymbol({
          color: [51, 78, 165, 0.2], // Semi-transparent fill
          outline: {
            color: [51, 78, 165, 1], // Solid outline
            width: 2,
          },
        }),
      });

      // Add the circle to the map
      this.mapView.graphics.add(circleGraphic);

      // Auto-zoom to the marked point and circle
      await this.mapView.goTo({
        target: circle.extent,
        zoom: 18, // Ensure a minimum zoom level
      });

      // Fetch location details within the circle
      this.isLoading = true;
      await this.fetchLocationDetails(point);
      this.isLoading = false;
    });
  }

  private async fetchAddress(point: Point) {
    try {
      const arcgisToken =
        this.arcgisService.getArcgisAuthInfoFromStorage()?.access_token;
      const url = `https://arcgis.accdata.org/server/sharing/servers/aaec0d98137b42c9bcdfd60e19a772e2/rest/services/World/GeocodeServer/reverseGeocode?f=json&location=${point.longitude},${point.latitude}&token=${arcgisToken}`;

      const response = await fetch(url);
      const data = await response.json();

      if (data && data.address && data.address.LongLabel) {
        this.siteIntelligenceService.setMarkedPointAddress(
          data.address.LongLabel,
        );
      } else {
        this.siteIntelligenceService.setMarkedPointAddress('Address not found');
      }
    } catch (error) {
      console.error('Error fetching address:', error);
      this.siteIntelligenceService.setMarkedPointAddress('Address not found');
    }
  }

  private async fetchLocationDetails(point: Point) {
    const searchDistance = parseFloat(
      (document.getElementById('myRange') as HTMLInputElement).value,
    );
    const measurementUnit = (
      document.querySelector('select') as HTMLSelectElement
    ).value;

    // Get all layers that match toolkit intelligence layer configurations
    const matchingLayers = this.mapView.map.layers.filter((layer) =>
      toolkitIntelligenceLayers.some((config) => config.title === layer.title),
    );

    if (matchingLayers.length === 0) {
      console.warn('No matching layers found for analysis.');
      return;
    }

    const formatDistance = (distance: number, unit: string): string => {
      const unitShortForm =
        {
          feet: 'ft',
          kilometers: 'km',
          meters: 'm',
          miles: 'mi',
        }[unit] || unit;

      const formattedDistance = Number.isInteger(distance)
        ? distance.toString()
        : distance.toFixed(3);
      return `${formattedDistance} ${unitShortForm}`;
    };

    // Helper function to get the correct feature layer(s) to query
    const getQueryableLayers = async (
      layer: Layer,
      layerTitle: string,
    ): Promise<{ layer: FeatureLayer; title: string }[]> => {
      if (layer.type === 'feature') {
        return [{ layer: layer as FeatureLayer, title: layerTitle }];
      } else if (layer.type === 'map-image') {
        const mapImageLayer = layer as MapImageLayer;
        // For CDFI Investment Areas, look for USACensusTractBoundaries_ExportFeatures sublayer
        if (layerTitle === 'CDFI Investment Areas') {
          const sublayer = mapImageLayer.sublayers.find(
            (sl: Sublayer) =>
              sl.title === 'USACensusTractBoundaries_ExportFeatures',
          );
          if (sublayer) {
            const featureLayer = await sublayer.createFeatureLayer();
            return featureLayer
              ? [{ layer: featureLayer, title: layerTitle }]
              : [];
          }
        }
        // For other map image layers, you might need to specify which sublayer to use
        // or use the first available sublayer
        if (mapImageLayer.sublayers && mapImageLayer.sublayers.length > 0) {
          const featureLayer = await mapImageLayer.sublayers
            .getItemAt(0)
            .createFeatureLayer();
          return featureLayer
            ? [{ layer: featureLayer, title: layerTitle }]
            : [];
        }
      } else if (layer.type === 'group') {
        const groupLayer = layer as GroupLayer;
        const layerConfig = toolkitIntelligenceLayers.find(
          (config) => config.title === layerTitle,
        );

        if (layerConfig?.subLayers && layerConfig.subLayers.length > 0) {
          // For group layers with subLayers config, get ALL matching sublayers
          const subLayers = groupLayer.layers;

          const queryableLayers: { layer: FeatureLayer; title: string }[] = [];

          // Find all sublayers that match the configured sublayer titles
          for (const configSubLayer of layerConfig.subLayers) {
            for (let i = 0; i < subLayers.length; i++) {
              const subLayer = subLayers.getItemAt(i);
              if (
                subLayer.type === 'feature' &&
                subLayer.title === configSubLayer.title
              ) {
                queryableLayers.push({
                  layer: subLayer as FeatureLayer,
                  title: `${layerTitle}: ${configSubLayer.title}`,
                });
              }
            }
          }

          // If we found matching sublayers, return them all
          if (queryableLayers.length > 0) {
            return queryableLayers;
          }

          // Fallback: return first feature layer if no title match found
          for (let i = 0; i < subLayers.length; i++) {
            const subLayer = subLayers.getItemAt(i);
            if (subLayer.type === 'feature') {
              console.log(
                `Found fallback feature subLayer in group layer ${layerTitle}:`,
                subLayer,
              );
              return [{ layer: subLayer as FeatureLayer, title: layerTitle }];
            }
          }
        } else {
          // Fallback: return first feature layer if no subLayers config
          const subLayers = groupLayer.layers;
          for (let i = 0; i < subLayers.length; i++) {
            const subLayer = subLayers.getItemAt(i);
            if (subLayer.type === 'feature') {
              return [{ layer: subLayer as FeatureLayer, title: layerTitle }];
            }
          }
        }
      }
      return [];
    };

    const results: SiteIntelligenceLayer[] = [];
    for (const layer of matchingLayers) {
      const layerConfig = toolkitIntelligenceLayers.find(
        (config) => config.title === layer.title,
      );
      if (!layerConfig) continue;

      // Get the queryable layer(s) (FeatureLayer or sublayers from MapImageLayer/GroupLayer)
      const queryableLayers = await getQueryableLayers(layer, layer.title);
      if (queryableLayers.length === 0) {
        console.warn(`Could not get queryable layers for: ${layer.title}`);
        continue;
      }

      // Process each queryable layer separately
      for (const {
        layer: queryableLayer,
        title: layerDisplayTitle,
      } of queryableLayers) {
        const query = queryableLayer.createQuery();
        query.geometry = point;
        query.distance = searchDistance;
        query.units = measurementUnit as
          | 'feet'
          | 'miles'
          | 'meters'
          | 'kilometers';
        query.outFields = ['*'];
        query.returnGeometry = true;

        if (layerConfig.analysisType === 'Closest Feature') {
          const queryResult = await queryableLayer.queryFeatures(query);

          if (queryResult.features.length > 0) {
            // Find the closest feature
            const closestFeature = queryResult.features.reduce(
              (prev: __esri.Graphic, curr: __esri.Graphic) => {
                const prevGeometry = webMercatorUtils.geographicToWebMercator(
                  prev.geometry,
                );
                const currGeometry = webMercatorUtils.geographicToWebMercator(
                  curr.geometry,
                );
                const pointGeometry =
                  webMercatorUtils.geographicToWebMercator(point);

                const prevDistance = geometryEngine.distance(
                  pointGeometry,
                  prevGeometry,
                  measurementUnit as __esri.LinearUnits,
                );
                const currDistance = geometryEngine.distance(
                  pointGeometry,
                  currGeometry,
                  measurementUnit as __esri.LinearUnits,
                );

                return prevDistance < currDistance ? prev : curr;
              },
            );

            // Store the feature for later highlighting (don't add to map yet)
            this.featureGraphics.set(layerDisplayTitle, [
              new Graphic({
                geometry: closestFeature.geometry,
                symbol: new SimpleFillSymbol({
                  color: [0, 0, 0, 0], // Transparent fill
                  outline: {
                    color: [245, 7, 245, 1], // Solid #f507f5 outline
                    width: 2,
                  },
                }),
              }),
            ]);

            // Add the feature's attributes to the results
            const approximateDistance = geometryEngine.distance(
              webMercatorUtils.geographicToWebMercator(point),
              webMercatorUtils.geographicToWebMercator(closestFeature.geometry),
            );
            results.push({
              layer_title: layerDisplayTitle,
              features: [
                {
                  attributes: {
                    ...closestFeature.attributes,
                    Approximate_Distance: formatDistance(
                      approximateDistance,
                      measurementUnit,
                    ),
                  } as FeatureAttributes,
                },
              ],
            });
          }
        } else if (layerConfig.analysisType === 'Proximity') {
          const queryResult = await queryableLayer.queryFeatures(query);
          if (queryResult.features.length > 0) {
            // Store features for later highlighting (don't add to map yet)
            const graphics = queryResult.features.map(
              (feature: __esri.Graphic) =>
                new Graphic({
                  geometry: feature.geometry,
                  symbol: new SimpleFillSymbol({
                    color: [0, 0, 0, 0], // Transparent fill
                    outline: {
                      color: [0, 0, 255, 1], // Solid blue outline
                      width: 2,
                    },
                  }),
                }),
            );
            this.featureGraphics.set(layerDisplayTitle, graphics);

            // Add all features' attributes to the results
            results.push({
              layer_title: layerDisplayTitle,
              features: queryResult.features.map((feature: __esri.Graphic) => ({
                attributes: {
                  ...feature.attributes,
                  Approximate_Distance: formatDistance(
                    geometryEngine.distance(
                      webMercatorUtils.geographicToWebMercator(point),
                      webMercatorUtils.geographicToWebMercator(
                        feature.geometry,
                      ),
                      measurementUnit as __esri.LinearUnits,
                    ),
                    measurementUnit,
                  ),
                },
              })),
            });
          }
        }
      }
    }

    this.siteIntelligenceService.setLocationDetails(
      results.length > 0 ? results : null,
    );

    // Initialize expandedCards array based on default expanded layers
    this.expandedCards = results.map((result) =>
      this.defaultExpandedLayers.includes(result.layer_title),
    );

    // Show outlines for initially expanded cards
    results.forEach((result, index) => {
      if (this.expandedCards[index]) {
        const graphics = this.featureGraphics.get(result.layer_title);
        if (graphics) {
          graphics.forEach((graphic) => this.mapView.graphics.add(graphic));
        }
      }
    });
  }

  toggleCard(index: number) {
    this.expandedCards[index] = !this.expandedCards[index];

    // Get the layer title for this card
    const layerTitle = this.locationDetails?.[index].layer_title;
    if (!layerTitle) return;

    if (this.expandedCards[index]) {
      // Show features for this layer
      const graphics = this.featureGraphics.get(layerTitle);
      if (graphics) {
        graphics.forEach((graphic) => this.mapView.graphics.add(graphic));
      }
    } else {
      // Hide features for this layer
      const graphics = this.featureGraphics.get(layerTitle);
      if (graphics) {
        graphics.forEach((graphic) => this.mapView.graphics.remove(graphic));
      }
    }
  }

  deleteMarkedPoint() {
    // Clear all graphics including feature highlights
    this.mapView.graphics.removeAll();
    this.featureGraphics.clear();
    this.siteIntelligenceService.clearData();

    // CLose lead info popup if it is open
    if (this.mapView?.popup && typeof this.mapView.popup.close === 'function') {
      this.mapView.popup.close();
    }

    // Remove lat and lon query parameters if present
    const url = new URL(window.location.href);
    const hasParams =
      url.searchParams.has('lat') ||
      url.searchParams.has('lon') ||
      url.searchParams.has('leadId');

    if (hasParams) {
      url.searchParams.delete('lat');
      url.searchParams.delete('lon');
      window.history.replaceState({}, '', url.toString());
    }
  }

  async refreshData() {
    // Reload analysis results for the last marked point
    const lastPointGraphic = this.mapView.graphics.find(
      (graphic) => graphic.geometry.type === 'point',
    );
    if (lastPointGraphic) {
      const point = lastPointGraphic.geometry as Point;
      this.isLoading = true;
      await this.fetchLocationDetails(point);
      this.isLoading = false;
    }
  }

  exportToPDF() {
    if (!this.locationDetails || !this.markedPointAddress) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    let yPos = 20;

    // Add title
    doc.setFontSize(16);
    doc.text('Site Analysis Report', pageWidth / 2, yPos, { align: 'center' });

    // Add timestamp
    yPos += 10;
    doc.setFontSize(10);
    doc.text(
      `Generated on: ${new Date().toLocaleString()}`,
      pageWidth / 2,
      yPos,
      { align: 'center' },
    );

    // Add marked location address
    yPos += 15;
    doc.setFontSize(12);
    doc.text('Location:', 20, yPos);
    doc.setFont('Arial', 'normal');
    yPos += 7;
    doc.text(this.markedPointAddress, 20, yPos);

    // Add details for each layer
    this.locationDetails.forEach((detail) => {
      yPos += 15;

      // Add layer title
      doc.setFont('Arial', 'bold');
      doc.text(detail.layer_title, 20, yPos);
      yPos += 7;

      // Add features
      detail.features.forEach((feature) => {
        const attributes = feature.attributes;
        const tableData = [];

        // Convert attributes to table rows based on layer type
        if (
          detail.layer_title === 'Regrid Nationwide Parcel Data' ||
          detail.layer_title === 'ACS 5YR Demographic Estimate Data by Tract' ||
          detail.layer_title === 'Difficult Development Areas' ||
          detail.layer_title === 'Low to Moderate Income Population by Tract' ||
          detail.layer_title === 'Rural Designations'
        ) {
          const fields = this.getLayerFields(detail.layer_title);
          for (const field of fields) {
            if (
              attributes[field.key] !== null &&
              attributes[field.key] !== undefined
            ) {
              tableData.push([
                field.displayName,
                attributes[field.key].toString(),
              ]);
            }
          }
        } else {
          // Handle other layers as before
          for (const [key, value] of Object.entries(attributes)) {
            if (value !== null && value !== undefined && key !== 'OBJECTID') {
              tableData.push([key, value.toString()]);
            }
          }
        }

        // Check if we need a new page
        if (yPos > doc.internal.pageSize.height - 20) {
          doc.addPage();
          yPos = 20;
        }

        // Add feature table
        autoTable(doc, {
          startY: yPos,
          head: [['Attribute', 'Value']],
          body: tableData,
          margin: { left: 20 },
          columnStyles: {
            0: { cellWidth: 80 },
            1: { cellWidth: 90 },
          },
          styles: {
            fontSize: 8,
            cellPadding: 2,
          },
        });

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        yPos = (doc as any).lastAutoTable.finalY + 10;
      });
    });

    // Save the PDF
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    doc.save(`site-analysis-${timestamp}.pdf`);
  }

  getLayerFields(layerTitle: string) {
    // First, try to find a direct match with main layer titles
    const layer = toolkitIntelligenceLayers.find((l) => l.title === layerTitle);

    if (layer) {
      // If the layer has sublayers, combine fields from all sublayers
      if (layer.subLayers && layer.subLayers.length > 0) {
        const allFields: { key: string; displayName: string }[] = [];
        layer.subLayers.forEach((subLayer) => {
          if (subLayer.fields) {
            allFields.push(...subLayer.fields);
          }
        });
        return allFields;
      }

      // Return regular fields if no sublayers
      return layer.fields || [];
    }

    // If no direct match found, search for sublayer with matching title
    for (const mainLayer of toolkitIntelligenceLayers) {
      if (mainLayer.subLayers && mainLayer.subLayers.length > 0) {
        const subLayer = mainLayer.subLayers.find(
          (sub) => sub.title === layerTitle,
        );
        if (subLayer && subLayer.fields) {
          return subLayer.fields;
        }
      }
    }

    return [];
  }

  isDateField(fieldKey: string): boolean {
    return [
      'll_last_refresh',
      'll_updated_at',
      'fema_flood_zone_data_date',
      'reviseddate',
      'usps_vacancy_date',
      'IMAGE_DATE',
      'PROD_DATE',
    ].includes(fieldKey);
  }
}
